import React, { useState, useRef, useEffect, useMemo } from 'react'
import { ChevronDown, ChevronUp, ChevronRight, Route, Mountain, Clock, MapPin, Filter, ArrowUpDown } from 'lucide-react'
import { UnifiedRoute } from '../../services/firebase/routes'
import { BookmarkButton } from '../common/BookmarkButton'
import {
  formatRouteLocation,
  getRouteTypeDisplay,
  getTotalDistance,
  getTotalElevationGain,
  calculateUnpavedPercentage,
  getRouteStageCount,
  getRouteRegion
} from '../../utils/routeDisplay'
import { getElevationGradientLevel, getElevationGradientColor, getElevationGradientLabel } from '../../services/elevationGradientService'
import { quickTimeEstimate } from '../../utils/timeCalculator'
import { getAllRegionData } from '../../services/regionFinder'

interface HomeRouteListPanelProps {
  routes: UnifiedRoute[]
  allRoutes?: UnifiedRoute[]  // Add this prop for unfiltered routes
  onRouteClick: (route: UnifiedRoute) => void
  selectedRouteId?: string | null
  filters: {
    country: string
    state: string
    region: string
    routeTypes: string[]
    distance: { min: number; max: number }
    terrain: { min: number; max: number }
    elevation: { min: number; max: number }
    elevationGradient: { min: number; max: number }
    isLoop: boolean
    clubs?: string[]
  }
  onFiltersChange: (filters: any) => void
  isSearching: boolean
  searchQuery: string
  onSearchChange: (query: string) => void
  selectedClub: any
  selectedLocations?: Set<string> | null
  onLocationsChange?: (locations: Set<string>, zoomToState?: string) => void
  selectedCountries?: Set<string> | null
  onCountriesChange?: (countries: Set<string>) => void
  selectedRegions?: Set<string>
  onRegionsChange?: (regions: Set<string>) => void
  showFilters?: boolean
}

export function HomeRouteListPanel({ 
  routes, 
  allRoutes,
  onRouteClick,
  selectedRouteId,
  filters,
  onFiltersChange,
  isSearching,
  searchQuery,
  onSearchChange,
  selectedClub,
  selectedLocations: propsSelectedLocations,
  onLocationsChange,
  selectedCountries: propsSelectedCountries,
  onCountriesChange,
  selectedRegions: propsSelectedRegions,
  onRegionsChange,
  showFilters = true
}: HomeRouteListPanelProps) {
  // Mobile detection (moved up so it can be used in initial state)
  const getIsMobile = () => window.innerWidth < 768 // md breakpoint in Tailwind
  
  const [isExpanded, setIsExpanded] = useState(!getIsMobile()) // Closed on mobile, open on desktop
  const contentRef = useRef<HTMLDivElement>(null)
  const [selectedLocations, setSelectedLocations] = useState<Set<string>>(propsSelectedLocations || new Set())
  const [selectedCountries, setSelectedCountries] = useState<Set<string>>(propsSelectedCountries || new Set(['Australia']))
  const [allRoutesSelected, setAllRoutesSelected] = useState(true)
  const [showSortDropdown, setShowSortDropdown] = useState(false)
  const [sortBy, setSortBy] = useState<'distance' | 'elevation' | 'name' | 'newest' | 'grade'>('newest')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const sortDropdownRef = useRef<HTMLDivElement>(null)
  const [showMoreFilters, setShowMoreFilters] = useState(false)
  const [expandedFilter, setExpandedFilter] = useState<string | null>(null)
  const [moreFiltersHeight, setMoreFiltersHeight] = useState(0)
  const moreFiltersRef = useRef<HTMLDivElement>(null)
  
  // Store all available locations from the initial unfiltered routes
  const [allAvailableLocationsByCountry, setAllAvailableLocationsByCountry] = useState<{ [key: string]: string[] }>({})
  // Store all available route types from the initial unfiltered routes
  const [allAvailableRouteTypes, setAllAvailableRouteTypes] = useState<string[]>([])
  // Store all available clubs from the initial unfiltered routes
  const [allAvailableClubs, setAllAvailableClubs] = useState<{ id: string; name: string }[]>([])
  const [selectedClubFilters, setSelectedClubFilters] = useState<Set<string>>(
    new Set(filters.clubs || [])
  )
  const [showClubDropdown, setShowClubDropdown] = useState(false)
  const clubDropdownRef = useRef<HTMLDivElement>(null)
  
  // Region filter state
  const [selectedRegions, setSelectedRegions] = useState<Set<string>>(propsSelectedRegions || new Set())
  const [activeStateForRegions, setActiveStateForRegions] = useState<string | null>(null)
  const [regionsExpanded, setRegionsExpanded] = useState(true)
  
  // Sync region state with props
  useEffect(() => {
    if (propsSelectedRegions) {
      setSelectedRegions(propsSelectedRegions)
    }
  }, [propsSelectedRegions])
  
  const [isMobile, setIsMobile] = useState(getIsMobile())
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => setIsMobile(getIsMobile())
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  // Sync selectedClubFilters when filters.clubs changes (e.g., from URL)
  useEffect(() => {
    if (filters.clubs) {
      setSelectedClubFilters(new Set(filters.clubs))
    }
  }, [filters.clubs])
  
  // Function to update club filters and notify parent
  const updateClubFilters = (newFilters: Set<string>) => {
    setSelectedClubFilters(newFilters)
    onFiltersChange({
      ...filters,
      clubs: Array.from(newFilters)
    })
  }
  
  // Get route type with terrain categorization for single routes
  const getRouteTypeWithTerrain = (route: UnifiedRoute) => {
    const routeType = getRouteTypeDisplay(route.routeType)
    
    // If it's a single route, categorize by terrain
    if (routeType === 'Single Route') {
      const unpavedPercentage = calculateUnpavedPercentage(route)
      if (unpavedPercentage >= 20) return 'Gravel' // 20-100% unpaved
      else if (unpavedPercentage >= 5) return 'Mixed' // 5-20% unpaved
      else return 'Road' // 0-5% unpaved
    }
    
    return routeType
  }
  
  // Get all unique route types from ALL routes (unfiltered) to show all tabs
  const allRouteTypes = useMemo(() => {
    const types = new Set<string>()
    const routesToCheck = allRoutes || routes // Use allRoutes if provided, otherwise fallback to filtered routes
    routesToCheck.forEach(route => {
      const routeType = getRouteTypeWithTerrain(route)
      types.add(routeType)
    })
    return Array.from(types).sort((a, b) => {
      // Custom sort order: Bikepacking, Road, Mixed, Gravel, Event, Permanent, Rail Trail, Bike Path
      const order = ['Bikepacking', 'Road', 'Mixed', 'Gravel', 'Event', 'Permanent', 'Rail Trail', 'Bike Path']
      return order.indexOf(a) - order.indexOf(b)
    })
  }, [allRoutes, routes])
  
  // Get available route types from filtered routes (for compatibility)
  const availableRouteTypes = useMemo(() => {
    const types = new Set<string>()
    routes.forEach(route => {
      const routeType = getRouteTypeWithTerrain(route)
      types.add(routeType)
    })
    return Array.from(types).sort((a, b) => {
      // Custom sort order: Bikepacking, Road, Mixed, Gravel, Event, Permanent, Rail Trail, Bike Path
      const order = ['Bikepacking', 'Road', 'Mixed', 'Gravel', 'Event', 'Permanent', 'Rail Trail', 'Bike Path']
      return order.indexOf(a) - order.indexOf(b)
    })
  }, [routes])
  
  // Get all unique clubs from routes
  const availableClubs = useMemo(() => {
    const clubsMap = new Map<string, string>()
    routes.forEach(route => {
      const routeData = route as any
      // Check submission data for club info
      if (routeData.submission?.clubId && routeData.submission?.clubName) {
        clubsMap.set(routeData.submission.clubId, routeData.submission.clubName)
      }
    })
    
    // Convert to sorted array
    return Array.from(clubsMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [routes])
  
  // Determine active state for regions based on selected states
  useEffect(() => {
    if (selectedLocations && selectedLocations.size === 1) {
      // If exactly one state is selected, set it as active for regions
      setActiveStateForRegions(Array.from(selectedLocations)[0])
    } else {
      // Multiple states or no states selected, clear active state
      setActiveStateForRegions(null)
    }
  }, [selectedLocations])
  
  // Store the original unfiltered routes for region calculation
  const [originalRoutes, setOriginalRoutes] = useState<UnifiedRoute[]>([])
  
  // Update original routes when routes prop changes (before filtering)
  useEffect(() => {
    if (routes.length > 0 && originalRoutes.length === 0) {
      setOriginalRoutes(routes)
    }
  }, [routes, originalRoutes.length])
  
  // Get available regions based on ALL routes in the state, not filtered routes
  const availableRegions = useMemo(() => {
    if (!activeStateForRegions) return []
    
    const regions = new Set<string>()
    // Use originalRoutes to ensure all regions are always available
    const routesToCheck = originalRoutes.length > 0 ? originalRoutes : routes
    
    routesToCheck.forEach(route => {
      const routeData = route as any
      let loc = null
      
      // Check route-level location first
      if (route.location) {
        loc = route.location
      } else if (routeData._statsOnlySegments?.[0]?.location) {
        loc = routeData._statsOnlySegments[0].location
      } else if (route.routes?.[0]?.location) {
        loc = route.routes[0].location
      }
      
      if (loc && loc.state) {
        const stateAbbr = {
          'Victoria': 'VIC',
          'New South Wales': 'NSW',
          'Queensland': 'QLD',
          'Western Australia': 'WA',
          'South Australia': 'SA',
          'Tasmania': 'TAS',
          'Northern Territory': 'NT',
          'Australian Capital Territory': 'ACT'
        }[loc.state] || loc.state
        
        // If this route is in the active state, add its region
        if (stateAbbr === activeStateForRegions && loc.region) {
          regions.add(loc.region)
        }
      }
    })
    
    return Array.from(regions).sort()
  }, [originalRoutes, routes, activeStateForRegions])
  
  // Get all unique locations from routes organized by country
  const locationsByCountry = useMemo(() => {
    const countries: { [key: string]: Set<string> } = {}
    
    routes.forEach(route => {
      const routeData = route as any
      let loc = null
      
      // Check route-level location first (new optimized approach)
      if (route.location) {
        loc = route.location
      } else if (routeData._statsOnlySegments?.[0]?.location) {
        loc = routeData._statsOnlySegments[0].location
      } else if (route.routes?.[0]?.location) {
        loc = route.routes[0].location
      }
      
      if (loc) {
        const country = loc.country
        if (!countries[country]) {
          countries[country] = new Set<string>()
        }
        
        if (loc.state) {
          const stateAbbr = {
            'Victoria': 'VIC',
            'New South Wales': 'NSW',
            'Queensland': 'QLD',
            'Western Australia': 'WA',
            'South Australia': 'SA',
            'Tasmania': 'TAS',
            'Northern Territory': 'NT',
            'Australian Capital Territory': 'ACT'
          }[loc.state] || loc.state
          countries[country].add(stateAbbr)
        }
      }
    })
    
    // Convert sets to sorted arrays
    const result: { [key: string]: string[] } = {}
    Object.keys(countries).forEach(country => {
      result[country] = Array.from(countries[country]).sort()
    })
    
    console.log('🗺️ LocationsByCountry:', result)
    return result
  }, [routes])
  
  // Only track state selections, not countries
  const allStates = useMemo(() => {
    const states: string[] = []
    Object.values(locationsByCountry).forEach(countryStates => {
      states.push(...countryStates)
    })
    return states
  }, [locationsByCountry])
  
  // Capture all available locations, route types, and clubs from original unfiltered routes
  useEffect(() => {
    if (Object.keys(locationsByCountry).length > 0 && Object.keys(allAvailableLocationsByCountry).length === 0) {
      console.log('📍 Setting allAvailableLocationsByCountry:', locationsByCountry)
      setAllAvailableLocationsByCountry(locationsByCountry)
    }
    if (allRouteTypes.length > 0 && allAvailableRouteTypes.length === 0) {
      setAllAvailableRouteTypes(allRouteTypes)
    }
    if (availableClubs.length > 0 && allAvailableClubs.length === 0) {
      setAllAvailableClubs(availableClubs)
    }
    // Also update originalRoutes if needed
    if (routes.length > 0 && originalRoutes.length === 0) {
      setOriginalRoutes(routes)
    }
  }, [locationsByCountry, allAvailableLocationsByCountry, allRouteTypes, allAvailableRouteTypes, availableClubs, allAvailableClubs, routes, originalRoutes.length])
  
  // Get all available states for initialization
  const allAvailableStates = useMemo(() => {
    const states: string[] = []
    Object.values(allAvailableLocationsByCountry).forEach(countryStates => {
      states.push(...countryStates)
    })
    return states
  }, [allAvailableLocationsByCountry])
  
  // Initialize with Australia selected by default
  useEffect(() => {
    // Only initialize if locations haven't been set yet (null)
    if (propsSelectedLocations === null) {
      // Start with empty state selections (country AU is selected by default)
      const newLocations = new Set<string>()
      setSelectedLocations(newLocations)
      onLocationsChange?.(newLocations)
    } else if (propsSelectedLocations !== null) {
      // If props have been set, use them
      setSelectedLocations(propsSelectedLocations)
    }
  }, [propsSelectedLocations, onLocationsChange])
  
  // Remove content height measurement - use fixed height instead
  
  // Measure more filters height for smooth animation
  useEffect(() => {
    if (moreFiltersRef.current) {
      setMoreFiltersHeight(moreFiltersRef.current.scrollHeight)
    }
  }, [showMoreFilters, expandedFilter])
  
  // Close dropdowns when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setShowSortDropdown(false)
      }
      if (clubDropdownRef.current && !clubDropdownRef.current.contains(event.target as Node)) {
        setShowClubDropdown(false)
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])
  
  // Route type colors
  const getRouteTypeColor = (routeType: string): string => {
    switch (routeType) {
      case 'Bikepacking': return '#8b5cf6' // Purple
      case 'Single Route': return '#3b82f6' // Blue
      case 'Road': return '#3b82f6' // Blue
      case 'Mixed': return '#f59e0b' // Amber
      case 'Gravel': return '#f97316' // Orange (same as elevation)
      case 'Event': return '#f59e0b' // Amber
      case 'Permanent': return '#10b981' // Emerald
      default: return '#6b7280' // Gray
    }
  }
  
  return (
    <div className="absolute top-4 left-4 w-80 backdrop-blur-2xl rounded-xl shadow-2xl shadow-black/50 bg-gradient-to-b from-black/60 to-gray-950/70 border border-white/[0.06] z-40 before:absolute before:inset-0 before:bg-gradient-to-br before:from-white/[0.02] before:to-transparent before:pointer-events-none ring-1 ring-inset ring-white/[0.05]">
      
      {/* Header */}
      <div className="p-2 select-none bg-black/20 border-b border-white/[0.03]">
        <div className={`flex items-center justify-between ${isExpanded ? 'mb-2' : ''}`}>
          <div 
            className="flex items-center gap-3 flex-1 cursor-pointer"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {/* CYA Routes Logo */}
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-lg flex items-center justify-center shadow-md p-0">
                <img 
                  src="/cyalogo.png" 
                  alt="CYA Routes Logo" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div className="flex items-center gap-2 whitespace-nowrap">
                <span className="text-lg font-bold text-gray-100 whitespace-nowrap">CYA Routes</span>
                <div className={`flex items-center gap-2 transition-opacity duration-300 ${isExpanded ? 'opacity-100' : 'opacity-0'}`}>
                  {selectedClub && (
                    <>
                      <span className="text-gray-500">•</span>
                      <span className="text-sm text-gray-300">{selectedClub.name}</span>
                    </>
                  )}
                </div>
                <span className={`text-xs text-gray-400 transition-all duration-300 ${isExpanded ? '' : 'ml-1'}`}>
                  ({routes.length} {routes.length === 1 ? 'route' : 'routes'})
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 rounded-lg hover:bg-white/[0.05] transition-all"
          >
            <ChevronDown className={`w-5 h-5 text-gray-400 transition-transform duration-500 ease-in-out ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        </div>
        
        {/* Search Bar - Only visible when expanded */}
        {isExpanded && (
          <input
            type="text"
            placeholder="Search routes..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            className="w-full px-3 py-1.5 text-xs bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500/30 focus:bg-white/[0.05] text-gray-200 placeholder-gray-400 transition-all"
          />
        )}
      </div>
      
      {/* Expandable Content */}
      <div 
        className="transition-all duration-500 ease-in-out overflow-hidden"
        style={{ 
          maxHeight: isExpanded ? '640px' : '0px',
          opacity: isExpanded ? 1 : 0
        }}
      >
        <div ref={contentRef}>
          {/* Quick Filters */}
          <div className="px-3 pt-1 pb-2 bg-black/20 border-b border-white/[0.03] space-y-2 relative z-50">
            {/* Club Filter Dropdown */}
            {allAvailableClubs.length > 0 && (
              <div className="relative" ref={clubDropdownRef}>
                <button
                  onClick={() => setShowClubDropdown(!showClubDropdown)}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors flex items-center gap-1 ${
                    selectedClubFilters.size > 0
                      ? 'bg-orange-600/60 text-white backdrop-blur-md border border-orange-400/60 shadow-md ring-1 ring-black/20'
                      : 'bg-orange-600/20 text-orange-200 hover:bg-orange-600/30 border border-orange-400/40 backdrop-blur-md'
                  }`}
                >
                  <span>
                    {selectedClubFilters.size === 0 
                      ? 'All Clubs'
                      : selectedClubFilters.size === 1
                      ? allAvailableClubs.find(club => selectedClubFilters.has(club.id))?.name || 'Club'
                      : `${selectedClubFilters.size} Clubs`
                    }
                  </span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${showClubDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showClubDropdown && (
                  <div className="absolute top-full left-0 mt-1 bg-black/70 backdrop-blur-2xl border border-white/[0.06] rounded-lg shadow-xl z-50 min-w-48 max-h-40 overflow-y-auto ring-1 ring-inset ring-white/[0.05]">
                    <div className="p-2 border-b border-white/[0.05]">
                      <button
                        onClick={() => {
                          if (selectedClubFilters.size === allAvailableClubs.length) {
                            // If all selected, deselect all
                            updateClubFilters(new Set())
                          } else {
                            // Select all
                            updateClubFilters(new Set(allAvailableClubs.map(club => club.id)))
                          }
                        }}
                        className="text-xs text-orange-400 hover:text-orange-300 transition-colors"
                      >
                        {selectedClubFilters.size === allAvailableClubs.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    {allAvailableClubs.map(club => (
                      <label
                        key={club.id}
                        className="flex items-center px-3 py-2 hover:bg-white/[0.05] cursor-pointer transition-colors"
                      >
                        <input
                          type="checkbox"
                          checked={selectedClubFilters.has(club.id)}
                          onChange={(e) => {
                            const newFilters = new Set(selectedClubFilters)
                            if (e.target.checked) {
                              newFilters.add(club.id)
                            } else {
                              newFilters.delete(club.id)
                            }
                            updateClubFilters(newFilters)
                          }}
                          className="text-orange-600 focus:ring-orange-500 mr-2 rounded"
                        />
                        <span className="text-xs text-gray-300">{club.name}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Route Types Pills */}
            <div className="flex flex-wrap gap-1">
                {/* All button */}
                <button
                  onClick={() => {
                    setAllRoutesSelected(true)
                    onFiltersChange({ ...filters, routeTypes: allAvailableRouteTypes })
                  }}
                  className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                    allRoutesSelected
                      ? 'bg-emerald-600/60 text-white backdrop-blur-md border border-emerald-400/60 shadow-md ring-1 ring-black/20'
                      : 'bg-emerald-600/20 text-emerald-200 hover:bg-emerald-600/30 border border-emerald-400/40 backdrop-blur-md'
                  }`}
                >
                  All
                </button>
                {/* Individual route type buttons */}
                {allAvailableRouteTypes.map(type => (
                  <button
                    key={type}
                    onClick={() => {
                      if (allRoutesSelected) {
                        // If "All" is selected, clicking a type selects only that type
                        setAllRoutesSelected(false)
                        onFiltersChange({ ...filters, routeTypes: [type] })
                      } else {
                        // Toggle individual types
                        const newTypes = filters.routeTypes.includes(type)
                          ? filters.routeTypes.filter(t => t !== type)
                          : [...filters.routeTypes, type]
                        
                        if (newTypes.length === 0) {
                          // If no types selected, go back to "All"
                          setAllRoutesSelected(true)
                          onFiltersChange({ ...filters, routeTypes: allAvailableRouteTypes })
                        } else {
                          // Just update with the selected types, even if all are selected
                          onFiltersChange({ ...filters, routeTypes: newTypes })
                        }
                      }
                    }}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                      !allRoutesSelected && filters.routeTypes.includes(type)
                        ? 'bg-emerald-600/60 text-white backdrop-blur-md border border-emerald-400/60 shadow-md ring-1 ring-black/20'
                        : type === 'Bike Path' && !filters.routeTypes.includes(type)
                        ? 'bg-emerald-600/10 text-emerald-300/50 hover:bg-emerald-600/20 hover:text-emerald-300/70 border border-emerald-400/20 backdrop-blur-md opacity-60'
                        : 'bg-emerald-600/20 text-emerald-200 hover:bg-emerald-600/30 border border-emerald-400/40 backdrop-blur-md'
                    }`}
                  >
                    {type}
                  </button>
                ))}
            </div>
            
            {/* Locations */}
            <div className="space-y-2 relative">
              {Object.entries(allAvailableLocationsByCountry).map(([country, states]) => (
                <div key={country} className="space-y-1">
                  {/* Country and States Row */}
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => {
                          const newCountries = new Set(selectedCountries)
                          const newLocations = new Set(selectedLocations)
                          const newRegions = new Set(selectedRegions)
                          
                          if (selectedCountries.has(country)) {
                            // Deselecting country - remove country, all its states and regions
                            newCountries.delete(country)
                            states.forEach(state => newLocations.delete(state))
                            newRegions.clear()
                            setActiveStateForRegions(null)
                          } else {
                            // Selecting country - add country and remove all its states and regions (country-level selection)
                            newCountries.add(country)
                            states.forEach(state => newLocations.delete(state))
                            newRegions.clear()
                            setActiveStateForRegions(null)
                          }
                          
                          setSelectedCountries(newCountries)
                          setSelectedLocations(newLocations)
                          setSelectedRegions(newRegions)
                          onLocationsChange?.(newLocations)
                          onCountriesChange?.(newCountries)
                          onRegionsChange?.(newRegions)
                        }}
                        className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                          selectedCountries.has(country)
                            ? 'bg-purple-600/60 text-white backdrop-blur-md border border-purple-400/60 shadow-md ring-1 ring-black/20'
                            : 'bg-purple-600/20 text-purple-200 hover:bg-purple-600/30 border border-purple-400/40 backdrop-blur-md'
                        }`}
                      >
                        {country === 'Australia' ? 'AU' : country === 'New Zealand' ? 'NZ' : country}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {states.map(state => (
                        <div key={state} className="flex items-center gap-1">
                          <button
                            onClick={() => {
                              const newLocations = new Set(selectedLocations)
                              const newCountries = new Set(selectedCountries)
                              
                              if (selectedLocations.has(state)) {
                                // For selected states, only handle deselection if:
                                // 1. It's not the active state for regions, OR
                                // 2. No regions are selected, OR  
                                // 3. Shift key is held (force deselect)
                                if (activeStateForRegions !== state || selectedRegions.size === 0) {
                                  // Deselecting state
                                  newLocations.delete(state)
                                  const newRegions = new Set(selectedRegions)
                                  // Clear regions if this was the active state
                                  if (activeStateForRegions === state) {
                                    newRegions.clear()
                                    setActiveStateForRegions(null)
                                  }
                                  setSelectedLocations(newLocations)
                                  setSelectedCountries(newCountries)
                                  setSelectedRegions(newRegions)
                                  onLocationsChange?.(newLocations)
                                  onCountriesChange?.(newCountries)
                                  onRegionsChange?.(newRegions)
                                }
                                // Otherwise, keep the state selected to preserve region selections
                              } else {
                                // Selecting state
                                newLocations.add(state)
                                // If selecting a state, deselect the country
                                newCountries.delete(country)
                                
                                // Only modify regions if switching to a different state
                                let newRegions = new Set(selectedRegions)
                                if (activeStateForRegions && activeStateForRegions !== state) {
                                  // Switching states - clear regions
                                  newRegions = new Set()
                                }
                                
                                setActiveStateForRegions(state)
                                setSelectedLocations(newLocations)
                                setSelectedCountries(newCountries)
                                setSelectedRegions(newRegions)
                                // Pass the state to zoom to when activating
                                onLocationsChange?.(newLocations, state)
                                onCountriesChange?.(newCountries)
                                onRegionsChange?.(newRegions)
                              }
                            }}
                            className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                              selectedLocations.has(state)
                                ? 'bg-purple-600/60 text-white backdrop-blur-md border border-purple-400/60 shadow-md ring-1 ring-black/20'
                                : 'bg-purple-600/20 text-purple-200 hover:bg-purple-600/30 border border-purple-400/40 backdrop-blur-md'
                            }`}
                          >
                            {state}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Region selection - shows when a single state is selected */}
            {activeStateForRegions && availableRegions.length > 0 && (
              <div className="mt-2 p-2 bg-black/10 rounded-lg border border-white/[0.05]">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs text-gray-400">
                    Filter by Region in {activeStateForRegions}
                    {selectedRegions.size > 0 && (
                      <span className="ml-1 text-purple-300">
                        ({selectedRegions.size} selected)
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setRegionsExpanded(!regionsExpanded)}
                    className="p-1 rounded hover:bg-white/[0.05] transition-colors"
                  >
                    <ChevronDown className={`w-3 h-3 text-gray-400 transition-transform ${regionsExpanded ? '' : 'rotate-180'}`} />
                  </button>
                </div>
                
                {regionsExpanded ? (
                  <>
                    <div className="flex flex-wrap gap-1">
                      <button
                        onClick={() => {
                          let newRegions: Set<string>
                          if (selectedRegions.size === availableRegions.length) {
                            // If all selected, deselect all
                            newRegions = new Set()
                          } else {
                            // Select all
                            newRegions = new Set(availableRegions)
                          }
                          setSelectedRegions(newRegions)
                          onRegionsChange?.(newRegions)
                        }}
                        className="px-2 py-0.5 rounded text-[10px] font-medium transition-colors bg-purple-600/20 text-purple-200 hover:bg-purple-600/30 border border-purple-400/40 backdrop-blur-md mb-1"
                      >
                        {selectedRegions.size === availableRegions.length ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
                      {availableRegions.map(region => (
                        <button
                          key={region}
                          onClick={() => {
                            const newRegions = new Set(selectedRegions)
                            if (selectedRegions.has(region)) {
                              newRegions.delete(region)
                            } else {
                              newRegions.add(region)
                            }
                            console.log('Region selection changed:', region, 'New regions:', Array.from(newRegions))
                            setSelectedRegions(newRegions)
                            onRegionsChange?.(newRegions)
                          }}
                          className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                            selectedRegions.has(region)
                              ? 'bg-purple-600/60 text-white backdrop-blur-md border border-purple-400/60 shadow-md ring-1 ring-black/20'
                              : 'bg-purple-600/20 text-purple-200 hover:bg-purple-600/30 border border-purple-400/40 backdrop-blur-md'
                          }`}
                        >
                          {region}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  // Collapsed view - show selected regions only
                  selectedRegions.size > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {Array.from(selectedRegions).map(region => (
                        <button
                          key={region}
                          onClick={() => {
                            const newRegions = new Set(selectedRegions)
                            newRegions.delete(region)
                            setSelectedRegions(newRegions)
                            onRegionsChange?.(newRegions)
                          }}
                          className="px-2 py-0.5 rounded text-[10px] font-medium transition-colors bg-purple-600/60 text-white backdrop-blur-md border border-purple-400/60 shadow-md ring-1 ring-black/20 group"
                        >
                          {region}
                          <span className="ml-1 opacity-0 group-hover:opacity-100 transition-opacity">×</span>
                        </button>
                      ))}
                    </div>
                  )
                )}
              </div>
            )}
            
            {/* Distance, Elevation, Region Accordions and Clear Button */}
            <div className="flex items-center gap-2">
              {/* Distance Accordion */}
              <button
                onClick={() => setExpandedFilter(expandedFilter === 'distance' ? null : 'distance')}
                className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors flex items-center gap-1 ${
                  expandedFilter === 'distance'
                    ? 'bg-blue-600/60 text-white backdrop-blur-md border border-blue-400/60 shadow-md ring-1 ring-black/20'
                    : 'bg-blue-600/20 text-blue-200 hover:bg-blue-600/30 border border-blue-400/40 backdrop-blur-md'
                }`}
              >
                <span>Distance</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${expandedFilter === 'distance' ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Elevation Accordion */}
              <button
                onClick={() => setExpandedFilter(expandedFilter === 'elevation' ? null : 'elevation')}
                className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors flex items-center gap-1 ${
                  expandedFilter === 'elevation'
                    ? 'bg-blue-600/60 text-white backdrop-blur-md border border-blue-400/60 shadow-md ring-1 ring-black/20'
                    : 'bg-blue-600/20 text-blue-200 hover:bg-blue-600/30 border border-blue-400/40 backdrop-blur-md'
                }`}
              >
                <span>Elevation</span>
                <ChevronDown className={`w-3 h-3 transition-transform ${expandedFilter === 'elevation' ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Clear All Button */}
              <button
                onClick={() => {
                  // Reset all filters to default values but keep location pills
                  onFiltersChange({
                    ...filters,
                    routeTypes: allAvailableRouteTypes,
                    distance: { min: 0, max: 500 },
                    elevation: { min: 0, max: 4000 },
                    elevationGradient: { min: 0, max: 4 },
                    terrain: { min: 0, max: 2 },
                    isLoop: false
                  })
                  setAllRoutesSelected(true)
                  updateClubFilters(new Set())
                  setExpandedFilter(null)
                  // Clear region selections when clearing filters
                  setSelectedRegions(new Set())
                  onRegionsChange?.(new Set())
                }}
                className="px-2 py-0.5 rounded text-[10px] font-medium transition-colors bg-black/30 text-gray-400 hover:bg-red-900/20 hover:text-red-300 border border-white/[0.06]"
              >
                Clear
              </button>
              
              {/* Sort Dropdown */}
              <div className="relative" ref={sortDropdownRef}>
                <button
                  onClick={() => setShowSortDropdown(!showSortDropdown)}
                  className="px-2 py-0.5 rounded text-[10px] font-medium transition-colors bg-black/30 text-gray-400 hover:bg-black/40 hover:text-gray-300 border border-white/[0.06] flex items-center gap-1"
                >
                  <ArrowUpDown className="w-3 h-3" />
                  <span>Sort</span>
                  {sortDirection === 'asc' ? (
                    <ChevronUp className="w-3 h-3" />
                  ) : (
                    <ChevronDown className="w-3 h-3" />
                  )}
                </button>
                
                {showSortDropdown && (
                  <div className="absolute right-0 mt-1 w-32 bg-black/70 backdrop-blur-2xl rounded-lg shadow-xl border border-white/[0.06] z-50 py-1 ring-1 ring-inset ring-white/[0.05]">
                    <button
                      onClick={() => {
                        if (sortBy === 'newest') {
                          // Toggle direction if already selected
                          setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')
                        } else {
                          setSortBy('newest')
                          setSortDirection('desc')
                        }
                        setShowSortDropdown(false)
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-white/[0.05] transition-colors ${
                        sortBy === 'newest' ? 'text-emerald-400' : 'text-gray-300'
                      }`}
                    >
                      {sortBy === 'newest' && sortDirection === 'asc' ? 'Oldest First' : 'Newest First'}
                    </button>
                    <button
                      onClick={() => {
                        if (sortBy === 'name') {
                          // Toggle direction if already selected
                          setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
                        } else {
                          setSortBy('name')
                          setSortDirection('asc')
                        }
                        setShowSortDropdown(false)
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-white/[0.05] transition-colors ${
                        sortBy === 'name' ? 'text-emerald-400' : 'text-gray-300'
                      }`}
                    >
                      {sortBy === 'name' && sortDirection === 'desc' ? 'Name Z-A' : 'Name A-Z'}
                    </button>
                    <button
                      onClick={() => {
                        if (sortBy === 'distance') {
                          // Toggle direction if already selected
                          setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')
                        } else {
                          setSortBy('distance')
                          setSortDirection('desc')
                        }
                        setShowSortDropdown(false)
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-white/[0.05] transition-colors ${
                        sortBy === 'distance' ? 'text-emerald-400' : 'text-gray-300'
                      }`}
                    >
                      {sortBy === 'distance' && sortDirection === 'asc' ? 'Distance (Short)' : 'Distance (Long)'}
                    </button>
                    <button
                      onClick={() => {
                        if (sortBy === 'elevation') {
                          // Toggle direction if already selected
                          setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')
                        } else {
                          setSortBy('elevation')
                          setSortDirection('desc')
                        }
                        setShowSortDropdown(false)
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-white/[0.05] transition-colors ${
                        sortBy === 'elevation' ? 'text-emerald-400' : 'text-gray-300'
                      }`}
                    >
                      {sortBy === 'elevation' && sortDirection === 'asc' ? 'Elevation (Low)' : 'Elevation (High)'}
                    </button>
                    <button
                      onClick={() => {
                        if (sortBy === 'grade') {
                          // Toggle direction if already selected
                          setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')
                        } else {
                          setSortBy('grade')
                          setSortDirection('desc')
                        }
                        setShowSortDropdown(false)
                      }}
                      className={`w-full px-3 py-1.5 text-left text-xs hover:bg-white/[0.05] transition-colors ${
                        sortBy === 'grade' ? 'text-emerald-400' : 'text-gray-300'
                      }`}
                    >
                      {sortBy === 'grade' && sortDirection === 'asc' ? 'Grade (Easy)' : 'Grade (Hard)'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          
          {/* Expandable Filter Section - Distance or Elevation */}
          {expandedFilter && (
            <div className="border-t border-white/[0.03] px-3 py-3 bg-black/20">
              {/* Distance Filter */}
              {expandedFilter === 'distance' && (
                <div>
                  <div className="text-xs text-gray-400 mb-2">
                    Distance: {filters.distance.min}-{filters.distance.max >= 500 ? '500+' : filters.distance.max}km
                  </div>
                  <div className="relative">
                    <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                      <span>0km</span>
                      <span>250km</span>
                      <span>500km+</span>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min="0"
                        max="500"
                        value={filters.distance.min}
                        onChange={(e) => onFiltersChange({
                          ...filters,
                          distance: {
                            min: parseInt(e.target.value),
                            max: Math.max(parseInt(e.target.value), filters.distance.max)
                          }
                        })}
                        className="absolute w-full h-1 bg-transparent appearance-none cursor-pointer z-20 range-slider"
                      />
                      <input
                        type="range"
                        min="0"
                        max="500"
                        value={filters.distance.max}
                        onChange={(e) => onFiltersChange({
                          ...filters,
                          distance: {
                            min: Math.min(filters.distance.min, parseInt(e.target.value)),
                            max: parseInt(e.target.value)
                          }
                        })}
                        className="absolute w-full h-1 bg-transparent appearance-none cursor-pointer z-20 range-slider"
                      />
                      <div className="relative w-full h-1 bg-white/10 rounded">
                        <div 
                          className="absolute h-1 bg-blue-500 rounded"
                          style={{
                            left: `${(filters.distance.min / 500) * 100}%`,
                            width: `${((filters.distance.max - filters.distance.min) / 500) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Elevation Filter */}
              {expandedFilter === 'elevation' && (
                <div>
                  <div className="text-xs text-gray-400 mb-2">
                    Elevation: {filters.elevation.min}-{filters.elevation.max >= 4000 ? '4000+' : filters.elevation.max}m
                  </div>
                  <div className="relative">
                    <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                      <span>0m</span>
                      <span>2000m</span>
                      <span>4000m+</span>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min="0"
                        max="4000"
                        value={filters.elevation.min}
                        onChange={(e) => onFiltersChange({
                          ...filters,
                          elevation: {
                            min: parseInt(e.target.value),
                            max: Math.max(parseInt(e.target.value), filters.elevation.max)
                          }
                        })}
                        className="absolute w-full h-1 bg-transparent appearance-none cursor-pointer z-20 range-slider"
                      />
                      <input
                        type="range"
                        min="0"
                        max="4000"
                        value={filters.elevation.max}
                        onChange={(e) => onFiltersChange({
                          ...filters,
                          elevation: {
                            min: Math.min(filters.elevation.min, parseInt(e.target.value)),
                            max: parseInt(e.target.value)
                          }
                        })}
                        className="absolute w-full h-1 bg-transparent appearance-none cursor-pointer z-20 range-slider"
                      />
                      <div className="relative w-full h-1 bg-white/10 rounded">
                        <div 
                          className="absolute h-1 bg-blue-500 rounded"
                          style={{
                            left: `${(filters.elevation.min / 4000) * 100}%`,
                            width: `${((filters.elevation.max - filters.elevation.min) / 4000) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Grade Range */}
                  <div className="relative pt-3 mt-3 border-t border-white/[0.05]">
                    <div className="text-[10px] text-gray-500 mb-1">Difficulty Grade</div>
                    <div className="flex justify-between text-[10px] text-gray-500 mb-1">
                      <span>Flat</span>
                      <span>Roll</span>
                      <span>Hilly</span>
                      <span>Climb</span>
                      <span>Alpine</span>
                    </div>
                    <div className="relative">
                      <input
                        type="range"
                        min="0"
                        max="4"
                        value={filters.elevationGradient.min}
                        onChange={(e) => onFiltersChange({
                          ...filters,
                          elevationGradient: {
                            min: parseInt(e.target.value),
                            max: Math.max(parseInt(e.target.value), filters.elevationGradient.max)
                          }
                        })}
                        className="absolute w-full h-1 bg-transparent appearance-none cursor-pointer z-20 range-slider"
                      />
                      <input
                        type="range"
                        min="0"
                        max="4"
                        value={filters.elevationGradient.max}
                        onChange={(e) => onFiltersChange({
                          ...filters,
                          elevationGradient: {
                            min: Math.min(filters.elevationGradient.min, parseInt(e.target.value)),
                            max: parseInt(e.target.value)
                          }
                        })}
                        className="absolute w-full h-1 bg-transparent appearance-none cursor-pointer z-20 range-slider"
                      />
                      <div className="relative w-full h-1 bg-white/10 rounded">
                        <div 
                          className="absolute h-1 bg-gradient-to-r from-green-500 via-yellow-500 to-red-500 rounded"
                          style={{
                            left: `${(filters.elevationGradient.min / 4) * 100}%`,
                            width: `${((filters.elevationGradient.max - filters.elevationGradient.min) / 4) * 100}%`
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
            </div>
          )}
          
          {/* Route List */}
          <div className="overflow-y-auto scrollbar-thin scrollbar-track-gray-800 scrollbar-thumb-gray-600 relative" style={{ maxHeight: '424px' }}>
            {isSearching ? (
              <div className="p-8 text-center">
                <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                <p className="text-gray-400 text-sm">Searching routes...</p>
              </div>
            ) : routes.length === 0 ? (
              <div className="p-8 text-center">
                <p className="text-gray-400 text-sm">No routes found</p>
              </div>
            ) : (
              // Filter routes by club if selected, then sort
              [...routes]
                .filter(route => {
                  // Apply club filter if specific clubs are selected
                  if (selectedClubFilters.size > 0) {
                    const routeData = route as any
                    const routeClubId = routeData.submission?.clubId
                    return routeClubId && selectedClubFilters.has(routeClubId)
                  }
                  return true
                })
                .sort((a, b) => {
                let result = 0
                switch (sortBy) {
                  case 'name':
                    result = a.name.localeCompare(b.name)
                    break
                  case 'distance':
                    result = getTotalDistance(a) - getTotalDistance(b)
                    break
                  case 'elevation':
                    result = getTotalElevationGain(a) - getTotalElevationGain(b)
                    break
                  case 'grade':
                    const aGrade = getElevationGradientLevel(getTotalElevationGain(a), getTotalDistance(a), a.routeType)
                    const bGrade = getElevationGradientLevel(getTotalElevationGain(b), getTotalDistance(b), b.routeType)
                    result = aGrade - bGrade
                    break
                  case 'newest':
                  default:
                    // Assuming routes have a createdAt or similar field
                    const aTime = (a as any).createdAt?.toMillis?.() || (a as any).createdAt || 0
                    const bTime = (b as any).createdAt?.toMillis?.() || (b as any).createdAt || 0
                    result = aTime - bTime
                    break
                }
                // Apply sort direction
                return sortDirection === 'asc' ? result : -result
              }).map((route, index) => {
                const distance = getTotalDistance(route)
                const elevation = getTotalElevationGain(route)
                const location = formatRouteLocation(route)
                const unpavedPercentage = calculateUnpavedPercentage(route)
                const stageCount = getRouteStageCount(route)
                const routeType = getRouteTypeWithTerrain(route)
                const elevationLevel = getElevationGradientLevel(elevation, distance, route.routeType)
                const elevationColor = getElevationGradientColor(elevationLevel)
                const elevationLabel = getElevationGradientLabel(elevationLevel)
                const isSelected = selectedRouteId === route.id
                const isEvenRow = index % 2 === 0
                
                // Get route image - prioritize highlight images
                const getRouteImage = () => {
                  if (route.photos && route.photos.length > 0) {
                    // First, check for main highlight image
                    if ((route as any).highlightImages?.main) {
                      const mainPhoto = route.photos.find(photo => photo.id === (route as any).highlightImages.main)
                      if (mainPhoto?.url) return mainPhoto.url
                    }
                    
                    // Then check for first additional highlight image
                    if ((route as any).highlightImages?.additional && (route as any).highlightImages.additional.length > 0) {
                      const firstAdditionalId = (route as any).highlightImages.additional[0]
                      const additionalPhoto = route.photos.find(photo => photo.id === firstAdditionalId)
                      if (additionalPhoto?.url) return additionalPhoto.url
                    }
                    
                    // Fall back to first photo with URL
                    const firstPhoto = route.photos.find(photo => photo.url)
                    if (firstPhoto?.url) return firstPhoto.url
                  }
                  return route.thumbnail || 'https://images.unsplash.com/photo-1551632811-561732d1e306?w=400&h=400&fit=crop'
                }
                
                return (
                  <div
                    key={route.id}
                    onClick={(e) => {
                      e.stopPropagation()
                      onRouteClick(route)
                      // Minimize panel in mobile mode when a route is clicked
                      if (isMobile) {
                        setIsExpanded(false)
                      }
                    }}
                    className={`group relative cursor-pointer transition-all duration-200 ease-in-out overflow-hidden px-2 py-1.5 ${
                      isSelected
                        ? 'bg-emerald-500/10 backdrop-blur-sm border-l-2 border-emerald-400/60'
                        : isEvenRow 
                          ? 'bg-black/20 hover:bg-black/30 border-l-2 border-transparent'
                          : 'bg-black/10 hover:bg-black/25 border-l-2 border-transparent'
                    }`}
                  >
                    <div className="flex gap-2">
                      {/* Square Image */}
                      <div className="w-16 h-16 flex-shrink-0 relative">
                        <img 
                          src={getRouteImage()}
                          alt={route.name}
                          className="w-full h-full object-cover rounded"
                          loading="lazy"
                        />
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        {/* Title and Location */}
                        <div className="mb-1">
                          <div className="flex items-start justify-between gap-1">
                            <h3 className="text-xs font-semibold truncate transition-colors duration-200 text-white group-hover:text-emerald-400">
                              {route.name}
                            </h3>
                            <BookmarkButton
                              routeId={route.id}
                              routeData={{
                                routeId: route.id,
                                routeName: route.name,
                                routeThumbnail: getRouteImage(),
                                routeDistance: distance,
                                routeElevation: elevation,
                                routeLocation: location,
                                routeType: routeType,
                                clubId: (route as any).submission?.clubId,
                                clubName: (route as any).submission?.clubName
                              }}
                              size="sm"
                              variant="default"
                              className="opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>
                          <div className="flex items-center gap-1 text-[10px] mt-0.5">
                            <MapPin className="w-2.5 h-2.5 flex-shrink-0 text-gray-500" />
                            <span className="truncate text-gray-400">{location}</span>
                          </div>
                        </div>
                        
                        {/* Stats Grid - Column Layout */}
                        <div className="grid grid-cols-3 gap-x-3 gap-y-1 text-[10px]">
                          {/* First column - Distance */}
                          <div className="flex items-center gap-0.5">
                            <Route className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                            <span className="font-medium text-gray-300">
                              {distance.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}km
                            </span>
                          </div>
                          
                          {/* Second column - Elevation */}
                          <div className="flex items-center gap-0.5">
                            <Mountain className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                            <span className="font-medium text-gray-300">
                              {elevation.toLocaleString()}m
                            </span>
                          </div>
                          
                          {/* Third column - Unpaved (if applicable) */}
                          {unpavedPercentage > 0 ? (
                            <div className="flex items-center gap-0.5">
                              <svg className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                              </svg>
                              <span className="font-medium text-gray-300">{unpavedPercentage}% unp.</span>
                            </div>
                          ) : (
                            <div></div>
                          )}
                          
                          {/* Fourth column (second row) - Time/Stages */}
                          {stageCount > 1 ? (
                            <div className="flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                              <span className="text-[10px] font-medium text-gray-300">{stageCount} Stages</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-0.5">
                              <Clock className="w-2.5 h-2.5 text-emerald-400 flex-shrink-0" />
                              <span className="text-[10px] font-medium text-gray-300">
                                {quickTimeEstimate(distance, elevation, unpavedPercentage, routeType)}
                              </span>
                            </div>
                          )}
                          
                          {/* Fifth column (second row) - Elevation grade */}
                          <div className="flex items-center gap-1">
                            <div 
                              className="w-2 h-2 rounded-full flex-shrink-0"
                              style={{ backgroundColor: elevationColor }}
                            />
                            <span className="text-[10px] text-gray-300">
                              {elevationLabel}
                            </span>
                          </div>
                          
                          {/* Sixth column (second row) - Route type outlined pill */}
                          <div className="flex items-center">
                            <span 
                              className="px-1.5 py-0.5 rounded text-[9px] font-medium border"
                              style={{
                                borderColor: getRouteTypeColor(routeType),
                                color: getRouteTypeColor(routeType)
                              }}
                            >
                              {routeType === 'Single Route' ? 'Single' : routeType}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
            {/* Bottom padding for scroll past end */}
            {routes.length > 0 && !isSearching && (
              <div className="h-24" />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}